import { useEffect, useState, type ReactNode } from "react";

type Page =
  | "home"
  | "about"
  | "solutions"
  | "approach"
  | "contact"
  | "privacy"
  | "terms"
  | "admin1985";
type Theme = "light" | "dark";

const themeStorageKey = "espoox-theme";

const pageTitles: Record<Page, string> = {
  home: "ESPooX — Privacy-conscious care awareness research",
  about: "About ESPooX",
  solutions: "ESPooX areas of work",
  approach: "Our responsible development approach",
  contact: "Contact ESPooX",
  privacy: "Privacy policy",
  terms: "Terms of use",
  admin1985: "Administration",
};

const headerTagline = "A Future Where Living Alone Does Not Mean Being Left Alone!";

const navItems: Array<{ page: Exclude<Page, "home">; label: string }> = [
  { page: "about", label: "About" },
  { page: "solutions", label: "Areas of work" },
  { page: "approach", label: "Our approach" },
  { page: "contact", label: "Contact" },
];

function pathToPage(pathname: string): Page {
  const page = pathname.replace(/^\//, "") as Page;
  return page in pageTitles ? page : "home";
}

function navigate(page: Page): void {
  const path = page === "home" ? "/" : `/${page}`;
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function getInitialTheme(): Theme {
  const savedTheme = window.localStorage.getItem(themeStorageKey);
  if (savedTheme === "light" || savedTheme === "dark") {
    return savedTheme;
  }

  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function App(): ReactNode {
  const [page, setPage] = useState<Page>(() => pathToPage(window.location.pathname));
  const [menuOpen, setMenuOpen] = useState(false);
  const [theme, setTheme] = useState<Theme>(getInitialTheme);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    window.localStorage.setItem(themeStorageKey, theme);
  }, [theme]);

  useEffect(() => {
    const onPopState = () => setPage(pathToPage(window.location.pathname));
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    document.title = pageTitles[page];
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [page]);

  const goTo = (nextPage: Page) => {
    navigate(nextPage);
    setPage(nextPage);
    setMenuOpen(false);
  };

  return (
    <div className="site-shell">
      <header className="site-header">
        <div className="site-header-inner">
          <a
            className="brand"
            href="/"
            onClick={(event) => {
              event.preventDefault();
              goTo("home");
            }}
            aria-label="ESPooX home"
          >
            <span className="brand-mark" aria-hidden="true">
              Ex
            </span>
            <span className="brand-name">espoox</span>
            <span className="page-title">{headerTagline}</span>
          </a>
          <nav
            id="primary-navigation"
            className={menuOpen ? "primary-navigation is-open" : "primary-navigation"}
            aria-label="Primary navigation"
          >
            {navItems.map((item) => (
              <a
                key={item.page}
                className={page === item.page ? "nav-link active" : "nav-link"}
                href={`/${item.page}`}
                onClick={(event) => {
                  event.preventDefault();
                  goTo(item.page);
                }}
              >
                {item.label}
              </a>
            ))}
          </nav>
          <div className="header-actions">
            <button
              className="icon-button theme-toggle"
              type="button"
              aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
              title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
              onClick={() =>
                setTheme((currentTheme) => (currentTheme === "dark" ? "light" : "dark"))
              }
            >
              {theme === "dark" ? (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <circle cx="12" cy="12" r="4" />
                  <path d="M12 2v2M12 20v2M4.93 4.93l1.42 1.42M17.65 17.65l1.42 1.42M2 12h2M20 12h2M4.93 19.07l1.42-1.42M17.65 6.35l1.42-1.42" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M20.5 15.5A8.5 8.5 0 0 1 8.5 3.5 8.5 8.5 0 1 0 20.5 15.5Z" />
                </svg>
              )}
            </button>
            <button
              className="menu-toggle"
              type="button"
              aria-label={menuOpen ? "Close navigation" : "Open navigation"}
              title={menuOpen ? "Close navigation" : "Open navigation"}
              aria-expanded={menuOpen}
              aria-controls="primary-navigation"
              onClick={() => setMenuOpen((open) => !open)}
            >
              <span className="sr-only">{menuOpen ? "Close navigation" : "Open navigation"}</span>
              <span
                className={menuOpen ? "hamburger-icon is-open" : "hamburger-icon"}
                aria-hidden="true"
              >
                <span />
                <span />
                <span />
              </span>
            </button>
          </div>
        </div>
      </header>

      <main>{renderPage(page, goTo)}</main>

      <footer className="site-footer">
        <div>
          <p className="footer-brand">ESPooX</p>
          <p>ESPooX is operated by ESPOOX PTY LTD, a software and technology company.</p>
        </div>
        <div className="footer-links" aria-label="Footer navigation">
          <button type="button" onClick={() => goTo("privacy")}>
            Privacy
          </button>
          <button type="button" onClick={() => goTo("terms")}>
            Terms
          </button>
          <button type="button" onClick={() => goTo("contact")}>
            Contact
          </button>
        </div>
        <p className="footer-note">© {new Date().getFullYear()} ESPOOX PTY LTD · Australia</p>
      </footer>
    </div>
  );
}

function renderPage(page: Page, goTo: (page: Page) => void): ReactNode {
  switch (page) {
    case "about":
      return <AboutPage goTo={goTo} />;
    case "solutions":
      return <SolutionsPage goTo={goTo} />;
    case "approach":
      return <ApproachPage goTo={goTo} />;
    case "contact":
      return <ContactPage />;
    case "privacy":
      return <PrivacyPage />;
    case "terms":
      return <TermsPage />;
    case "admin1985":
      return <AdminPage />;
    default:
      return <HomePage goTo={goTo} />;
  }
}

function AdminPage(): ReactNode {
  const authError = new URLSearchParams(window.location.search).get("error");

  return (
    <section className="admin-page">
      <section className="admin-shell section-container" aria-labelledby="admin-title">
        <div className="admin-sidebar">
          <div className="eyebrow">ESPooX administration</div>
          <h1 id="admin-title">Manage the public website carefully.</h1>
          <p>
            The administration area is protected. Sign in with an approved identity provider to
            continue.
          </p>
        </div>
        <div className="admin-login-card">
          <h2>Administrator sign-in</h2>
          {authError === "provider_not_configured" ? (
            <p className="admin-auth-alert" role="alert">
              Administrator sign-in is not available yet. An operator must configure the approved
              identity provider and session settings first.
            </p>
          ) : null}
          <p>
            Access is granted only to explicitly provisioned accounts. A successful Google or
            Microsoft sign-in does not create administrator access automatically.
          </p>
          <div className="admin-provider-actions">
            <a className="button" href="/api/v1/admin/auth/google/start">
              Continue with Google
            </a>
            <a className="button button-quiet" href="/api/v1/admin/auth/microsoft/start">
              Continue with Microsoft
            </a>
          </div>
          <p className="fine-print">
            If your account is not provisioned, contact a Super Admin. Do not use this area for
            sensitive or emergency information.
          </p>
        </div>
      </section>
    </section>
  );
}

function HomePage({ goTo }: { goTo: (page: Page) => void }): ReactNode {
  return (
    <>
      <section className="hero section-container">
        <div className="eyebrow">Independent living · aged care · responsible research</div>
        <h1>More independence. More awareness. Less intrusion.</h1>
        <p className="hero-copy">
          ESPooX is developing privacy-conscious sensing technology to recognise potentially
          important safety events and changes in everyday movement. Our goal is to help people live
          independently and give families and care teams information that can support timely
          decisions.
        </p>
        <div className="button-row">
          <button className="button" type="button" onClick={() => goTo("solutions")}>
            Explore the concept
          </button>
          <button className="button button-quiet" type="button" onClick={() => goTo("contact")}>
            Discuss collaboration
          </button>
        </div>
        <div className="hero-orbit" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
      </section>
      <section className="section-container section-spaced">
        <SectionIntro
          eyebrow="The challenge"
          title="Important changes do not always happen during a check-in."
          copy="People living alone may not be able to call for help after an incident. Care teams make difficult decisions with limited time and incomplete information. Better awareness should not require giving up dignity or independence."
        />
        <div className="card-grid four-up">
          <StatusCard
            status="In research"
            title="Possible safety events"
            copy="Exploring whether movement patterns may indicate a fall or another event that a trusted person should review. A possible event is not a confirmed diagnosis."
          />
          <StatusCard
            status="In research"
            title="Everyday activity"
            copy="Investigating room-level movement and changes in routines, without assuming that every variation signals a problem."
          />
          <StatusCard
            status="Research direction"
            title="Movement over time"
            copy="Longer-term mobility and personal routine patterns may help identify changes worth discussing, subject to validation."
          />
          <StatusCard
            status="In design"
            title="Context for care teams"
            copy="Considering how relevant information could reach authorised contacts and care teams while keeping human judgement central."
          />
        </div>
      </section>
      <section className="section-container section-spaced">
        <SectionIntro
          eyebrow="Who we are learning from"
          title="Start with the people and settings that matter."
          copy="ESPooX is focused on understanding real needs before treating a concept as a product."
        />
        <div className="card-grid two-up">
          <StatusCard
            status="Priority audience"
            title="Independent living"
            copy="Explore how greater awareness of possible incidents and changes in daily activity could support choice, dignity, and independence—with access limited to nominated people."
          />
          <StatusCard
            status="Priority audience"
            title="Residential aged care"
            copy="Learn how context around night-time movement, possible incidents, and changing routines might fit care workflows and support staff review—not replace carers."
          />
        </div>
      </section>
      <section className="band section-spaced">
        <div className="section-container split-layout">
          <div>
            <div className="eyebrow">Privacy and research status</div>
            <h2>A new layer of awareness, not another layer of surveillance.</h2>
          </div>
          <p>
            ESPooX is in product and research development. We are exploring contactless approaches
            as an alternative to continuous identifiable video; actual sensing, privacy safeguards,
            and performance must be established and validated before product claims are made.
          </p>
        </div>
      </section>
      <section className="section-container section-spaced">
        <SectionIntro
          eyebrow="Research and collaboration"
          title="Shape technology around the people who will use it."
          copy="We welcome learning from people living alone, family supporters, carers, aged-care operators, and research partners. Their experience can help define which problems deserve attention and how future concepts should be evaluated."
        />
        <Callout
          title="Share a care challenge or research question."
          copy="Start with a real situation, existing practice, or measurable question. Described capabilities are concepts under development, not a currently available monitoring service."
          action="Contact ESPooX"
          onAction={() => goTo("contact")}
        />
      </section>
    </>
  );
}

function AboutPage({ goTo }: { goTo: (page: Page) => void }): ReactNode {
  return (
    <PageFrame
      eyebrow="About ESPooX"
      title="Technology built around real care challenges."
      intro="ESPooX is an Australian software and technology company developing an independent direction in privacy-conscious ambient sensing and intelligent care awareness. The sensing concepts described here remain in development and research."
    >
      <div className="content-grid">
        <div>
          <h2>Why we exist</h2>
          <p>
            We are exploring how technology might help people live independently and help families
            and care teams notice potentially meaningful changes. That starts with understanding
            everyday experience, making trade-offs visible, and changing direction when evidence
            points elsewhere.
          </p>
        </div>
        <div>
          <h2>Working principles</h2>
          <ul className="check-list">
            <li>Privacy and security by design</li>
            <li>Evidence before confident claims</li>
            <li>Human oversight for consequential decisions</li>
            <li>Accessible, understandable experiences</li>
            <li>Clear labels for research concepts and validated capabilities</li>
          </ul>
        </div>
      </div>
      <Callout
        title="Company identity"
        copy="ESPooX is operated by ESPOOX PTY LTD, a software and technology company. Public identifiers and publication-sensitive details will be added only after verification and approval."
        action="Talk with us"
        onAction={() => goTo("contact")}
      />
    </PageFrame>
  );
}

function SolutionsPage({ goTo }: { goTo: (page: Page) => void }): ReactNode {
  const solutions = [
    [
      "In research",
      "Independent living awareness",
      "Exploring how contactless sensing might surface possible safety events or meaningful changes in activity for a person and their nominated supporters. No guarantee of safety or emergency response is implied.",
    ],
    [
      "In research",
      "Residential aged-care context",
      "Investigating how information about possible incidents, night-time movement, and routines could fit real care workflows while leaving judgement and response with care teams.",
    ],
    [
      "Research direction",
      "Mobility and everyday routines",
      "Longer-term movement patterns and changes from personal routines are areas of research, not clinical assessments or validated risk scores.",
    ],
    [
      "In design",
      "Privacy-conscious awareness",
      "The product direction prioritises dignity, consent, and authorised access, while actual sensing and privacy safeguards remain subject to implementation and review.",
    ],
  ];
  return (
    <PageFrame
      eyebrow="Areas of work"
      title="Explore the direction. Understand what is still being learned."
      intro="ESPooX is developing concepts for people living alone and residential aged care. The capabilities below are in research or design; none should be read as a commercially available, clinically validated, or emergency-response service."
    >
      <div className="card-grid two-up">
        {solutions.map(([status, title, copy]) => (
          <StatusCard key={title} status={status} title={title} copy={copy} />
        ))}
      </div>
      <Callout
        title="Help define what useful care technology should do."
        copy="We are interested in discovery conversations and research collaboration with individuals, families, carers, operators, and researchers."
        action="Discuss research"
        onAction={() => goTo("contact")}
      />
    </PageFrame>
  );
}

function ApproachPage({ goTo }: { goTo: (page: Page) => void }): ReactNode {
  const steps = [
    "Listen to people living alone, family supporters, carers, and care operators.",
    "Understand existing check-ins, workflows, constraints, and what people consider meaningful.",
    "Define consent, privacy, accessibility, safety, and human-oversight requirements before prototyping.",
    "Evaluate usability, missed and incorrect alerts, environmental limits, and workflow fit.",
    "Publish only what evidence supports—or change direction when it does not.",
  ];
  return (
    <PageFrame
      eyebrow="Our approach"
      title="Research first. Validation before claims."
      intro="ESPooX’s sensing and care-awareness concepts are under development. They are not a substitute for professional judgement, carers, or emergency services."
    >
      <ol className="steps">
        {steps.map((step, index) => (
          <li key={step}>
            <span>{String(index + 1).padStart(2, "0")}</span>
            <p>{step}</p>
          </li>
        ))}
      </ol>
      <Callout
        title="Responsible by default"
        copy="Co-design, consent, privacy, false-alarm burden, accessibility, and measurable evaluation belong in the research—not as a final polish step."
        action="Discuss collaboration"
        onAction={() => goTo("contact")}
      />
    </PageFrame>
  );
}

function ContactPage(): ReactNode {
  return (
    <PageFrame
      eyebrow="Contact"
      title="Help define what useful care technology should do."
      intro="We welcome discovery conversations with people living alone, family supporters, carers, residential aged-care teams, operators, and research organisations. This is not an emergency reporting channel."
    >
      <div className="contact-panel">
        <div>
          <p className="contact-label">ESPOOX PTY LTD</p>
          <h2>Let’s begin with the situation.</h2>
          <p>
            We are interested in the real situation, current workarounds, privacy and consent needs,
            and what a useful outcome would need to demonstrate. ESPooX sensing concepts are not
            currently presented as a generally available service.
          </p>
        </div>
        <a className="email-link" href="mailto:support@espoox.com">
          support@espoox.com
        </a>
      </div>
      <p className="fine-print">
        Please do not email sensitive health, personal, or emergency information. Contacting ESPooX
        does not enrol anyone in research or a pilot; any participation would require a separate,
        clearly described process.
      </p>
    </PageFrame>
  );
}

function PrivacyPage(): ReactNode {
  return (
    <PageFrame
      eyebrow="Privacy"
      title="A clear policy should describe what actually happens."
      intro="This foundation does not yet collect contact-form submissions or run analytics. The final published policy will be updated when processing, hosting, cookies, and vendors are verified."
    >
      <div className="prose">
        <h2>Current foundation</h2>
        <p>
          This website foundation is designed to keep public content separate from future
          application data. Do not send sensitive personal, health, financial, or emergency
          information through the current email route.
        </p>
        <h2>Before launch</h2>
        <p>
          ESPOOX PTY LTD will document the actual information collected, purposes, hosting and
          processors, retention, security practices, access and correction requests, cookies,
          analytics, and complaint contact. Legal review is required before publication.
        </p>
      </div>
    </PageFrame>
  );
}

function TermsPage(): ReactNode {
  return (
    <PageFrame
      eyebrow="Terms"
      title="Information should be useful without overstating what it is."
      intro="These foundation notes are not a final legal notice. A reviewed Terms of Use page will be published before the website launch."
    >
      <div className="prose">
        <h2>Informational purpose</h2>
        <p>
          Website content may describe research directions, software work, and development ideas. It
          is not medical, financial, legal, emergency, or professional advice.
        </p>
        <h2>Accuracy and availability</h2>
        <p>
          We will maintain reasonable care over published information, but concepts and availability
          can change. Final terms will address intellectual property, external links, limitations,
          applicable law, and contact details after review.
        </p>
      </div>
    </PageFrame>
  );
}

function PageFrame({
  eyebrow,
  title,
  intro,
  children,
}: {
  eyebrow: string;
  title: string;
  intro: string;
  children: ReactNode;
}): ReactNode {
  return (
    <section className="section-container page-frame">
      <div className="eyebrow">{eyebrow}</div>
      <h1>{title}</h1>
      <p className="page-intro">{intro}</p>
      {children}
    </section>
  );
}

function SectionIntro({
  eyebrow,
  title,
  copy,
}: {
  eyebrow: string;
  title: string;
  copy: string;
}): ReactNode {
  return (
    <div className="section-intro">
      <div className="eyebrow">{eyebrow}</div>
      <h2>{title}</h2>
      <p>{copy}</p>
    </div>
  );
}
function StatusCard({
  status,
  title,
  copy,
}: {
  status: string;
  title: string;
  copy: string;
}): ReactNode {
  return (
    <article className="status-card">
      <span className="status-pill">{status}</span>
      <h3>{title}</h3>
      <p>{copy}</p>
    </article>
  );
}
function Callout({
  title,
  copy,
  action,
  onAction,
}: {
  title: string;
  copy: string;
  action: string;
  onAction: () => void;
}): ReactNode {
  return (
    <aside className="callout">
      <div>
        <h2>{title}</h2>
        <p>{copy}</p>
      </div>
      <button type="button" className="button button-light" onClick={onAction}>
        {action}
      </button>
    </aside>
  );
}

export default App;
